/**
 * ImpactScoresMockPage.tsx — Mock host page for VER-59 Impact Scores tab.
 *
 * PURPOSE:
 *   This page exists ONLY because VER-27 (participant detail page) is not
 *   yet built (Phase 1 / Sean). It provides a standalone route for
 *   design review and QA without touching Navigator.tsx or Routes.tsx.
 *
 *   Route suggestion (add manually to Routes.tsx when ready for preview):
 *     <Route path="/assess/impact-preview" element={<ImpactScoresMockPage />} />
 *
 * REMOVAL:
 *   Delete this file once ImpactScoresTab is mounted on VER-27's real tab
 *   panel. The tab component itself (ImpactScoresTab.tsx) is the keeper.
 *
 * RBAC:
 *   TODO [RBAC]: This preview page is unguarded. Add permit() before exposing
 *   on any route. Run verbal-rbac-review before merge to a non-feature branch.
 */

import React from "react";
import { Box, Divider, Tab, Tabs, Typography } from "@mui/material";
import { ImpactScoresTab } from "./ImpactScoresTab";

// Minimal tab panel wrapper — mirrors the pattern used in programme detail pages.
const TabPanel = ({
    children,
    value,
    index,
}: {
    children: React.ReactNode;
    value: number;
    index: number;
}): JSX.Element => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`impact-tabpanel-${index}`}
        aria-labelledby={`impact-tab-${index}`}
    >
        {value === index && <Box pt={3}>{children}</Box>}
    </div>
);

export const ImpactScoresMockPage = (): JSX.Element => {
    const [tab, setTab] = React.useState(0);

    return (
        <Box p={4} width={1}>
            {/* Page header — simulates what VER-27 will provide */}
            <Box mb={3}>
                <Typography variant="overline" color="textSecondary">
                    Participant detail — mock host (VER-27 not yet built)
                </Typography>
                <Typography variant="h3" component="h1" gutterBottom>
                    Participant Overview
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Mock host page for VER-59 Impact Scores tab. When VER-27 ships, mount{" "}
                    <code>&lt;ImpactScoresTab /&gt;</code> inside its tab panel and delete this file.
                </Typography>
            </Box>

            <Divider />

            {/* Tab bar — mirrors VER-27's expected structure */}
            <Tabs value={tab} onChange={(_e, v: number) => setTab(v)} aria-label="Participant detail tabs">
                <Tab label="Overview" id="impact-tab-0" aria-controls="impact-tabpanel-0" />
                <Tab label="Impact Scores" id="impact-tab-1" aria-controls="impact-tabpanel-1" />
                <Tab label="Sessions" id="impact-tab-2" aria-controls="impact-tabpanel-2" disabled />
            </Tabs>

            <TabPanel value={tab} index={0}>
                <Typography variant="body1" color="textSecondary">
                    Overview tab placeholder — will be built as part of VER-27.
                </Typography>
            </TabPanel>

            <TabPanel value={tab} index={1}>
                {/* The real deliverable. assessmentId="mock-1" simulates a real ID. */}
                <ImpactScoresTab assessmentId="mock-1" />
            </TabPanel>

            <TabPanel value={tab} index={2}>
                <Typography variant="body1" color="textSecondary">
                    Sessions tab placeholder.
                </Typography>
            </TabPanel>
        </Box>
    );
};
